import { practiceDataService } from "./practice-data.service";
import { uuid } from "@/lib/id";
import type { Mode, SpeechRating } from "@/features/vortrag/grade";

/**
 * Gehaltene Vortragsrunden (liegen in `practice_data.speechRounds`, siehe
 * SCHEMA.md). Gespeichert wird jede Runde, damit sich die Verbesserung über
 * die Runden hinweg zeigen lässt – das ist der Zweck des Modus, nicht die
 * Einzelnote. Reines Anhängen, es wird nie eine Runde geändert oder gelöscht.
 */

export interface SpeechRound {
  id: string;
  topic: string;
  mode: Mode;
  audience: string;
  round: number;
  seconds: number;
  transcript: string;
  rating: SpeechRating;
  content_pct: number;
  method_pct: number;
  created: string;
}

function newId(): string {
  return uuid();
}

function toRound(r: Record<string, unknown>, topicId: string): SpeechRound {
  return {
    id: String(r.id ?? ""),
    topic: topicId,
    mode: (r.mode === "feynman" ? "feynman" : "recall") as Mode,
    audience: String(r.audience ?? ""),
    round: Number(r.round ?? 1),
    seconds: Number(r.seconds ?? 0),
    transcript: String(r.transcript ?? ""),
    rating: (r.rating ?? {}) as SpeechRating,
    content_pct: Number(r.content_pct ?? 0),
    method_pct: Number(r.method_pct ?? 0),
    created: String(r.created ?? ""),
  };
}

function fromRound(s: SpeechRound): Record<string, unknown> {
  const { id, mode, audience, round, seconds, transcript, rating, content_pct, method_pct, created } = s;
  return { id, mode, audience, round, seconds, transcript, rating, content_pct, method_pct, created };
}

export interface SpeechInput {
  topic: string;
  mode: Mode;
  audience: string;
  round: number;
  seconds: number;
  transcript: string;
  rating: SpeechRating;
  content_pct: number;
  method_pct: number;
}

export const speechService = {
  async listByTopic(topicId: string, mode?: Mode): Promise<SpeechRound[]> {
    const { speechRounds } = await practiceDataService.read(topicId);
    const rounds = speechRounds.map((r) => toRound(r, topicId));
    return (mode ? rounds.filter((r) => r.mode === mode) : rounds).sort((a, b) =>
      a.created.localeCompare(b.created)
    );
  },

  async save(input: SpeechInput): Promise<SpeechRound> {
    const round: SpeechRound = { ...input, id: newId(), created: new Date().toISOString() };
    const { speechRounds } = await practiceDataService.read(input.topic);
    const next = [...speechRounds, fromRound(round)];
    await practiceDataService.write(input.topic, { speechRounds: next });
    return round;
  },
};
